// prepaidRouting.js — shared §12b prepaid-contract → IS-leaf resolver.
//
// Session F: extracts the account-label → IS-leaf resolution that was hand-
// mirrored in runFullModel §12b (engine booking) and VendorDashboard's
// displayVendors overlay (SC-3 resolve + sc-rework-1 sum). The three mirrors were
// proven logically identical at Phase 0; this is the single source of truth.
//
// PURE: never warns, never books. It returns a {lineKey, reason} verdict and lets
// each caller act — the ENGINE warns on skip + books at isLines[lineKey]; the
// OVERLAY stays silent + sums by vendor id. Slug-first trusts c.is_line BLINDLY
// (not validated against the map) because both call sites historically did so —
// this is a structure-only refactor, it fixes nothing.
//
// 2026-10-13 (Brian, SubBase onboarding) -- THIS FILE IS ALSO THE ONE OWNER OF "A PREPAID TEMPLATE ROW -> ITS IS LINE"
// (resolvePrepaidRowLine, at the foot). A chart that repeats a line name across sections ("Software" under COGS, S&M,
// R&D and G&A) cannot be resolved by the name alone, so a row that NAMES ITS SECTION (the prepaid template's
// is_category column) resolves by the SAME rule a vendor row uses: section + name -> buildVendorIsLineKey. The key rule
// is IMPORTED from its owner (utils/isLineKey.js), never respelled here -- that file owns the KEY (a pure string rule that
// knows no company); this file owns the RESOLUTION against one company's lines, which is why the composition lives here.
// resolvePrepaidContractLine below is UNCHANGED: a stored contract carries no section, so the engine, the Dashboard and
// every stored row resolve exactly as before.
import { buildVendorIsLineKey, CAT_SLUG_BY_DISPLAY } from '../utils/isLineKey';

// Build the reverse map: IS-leaf label → Set of leaf ids. Leaves only — headers
// and totals carry a `.type`, so `!l.type` filters them out. One pass.
// EXACTLY the build that lived inline at runFullModel:426-432 / VendorDashboard:1768-1774.
export function buildPrepaidLabelToLine(isLines) {
  const labelToLine = new Map();
  (isLines || []).forEach(l => {
    if (l && !l.type && l.id && l.label) {        // leaves only (headers/totals carry .type)
      if (!labelToLine.has(l.label)) labelToLine.set(l.label, new Set());
      labelToLine.get(l.label).add(l.id);
    }
  });
  return labelToLine;
}

// Resolve one prepaid contract to its IS leaf.
// Returns { lineKey, reason, ids? }:
//   - slug-first: c.is_line present → { lineKey: c.is_line, reason: 'slug' }
//       TRUST THE SLUG BLINDLY — do not validate against labelToLine (both call
//       sites did so un-validated; preserve that).
//   - else resolve c.account via the map:
//       0 matches  → { lineKey: null, reason: 'no-match' }
//       >1 matches → { lineKey: null, reason: 'ambiguous', ids: [...ids] }
//       1 match    → { lineKey: <id>,  reason: 'label' }
// NEVER warns, NEVER books.
export function resolvePrepaidContractLine(c, labelToLine) {
  if (c.is_line) return { lineKey: c.is_line, reason: 'slug' };
  const ids = labelToLine.get(c.account);
  if (!ids) return { lineKey: null, reason: 'no-match' };
  if (ids.size > 1) return { lineKey: null, reason: 'ambiguous', ids: [...ids] };
  return { lineKey: [...ids][0], reason: 'label' };
}

// ---- A PREPAID TEMPLATE ROW (2026-10-13) -------------------------------------------------------------------------
// A row is { account, is_category?, is_line? }: `account` is the template's gl_line_name (or, when that cell is blank,
// the vendor name -- the parser's fallback), `is_category` the template's section cell.

// The section a row names, trimmed; '' when it names none.
function prepaidRowSection(row) {
  return row && row.is_category != null ? String(row.is_category).trim() : '';
}

// The line key of a row that names its section: THE VENDOR RULE (section + name), called -- not copied.
// '' when the row names no section, an unknown section, or a name with nothing to key on.
export function prepaidRowSectionKey(row) {
  const section = prepaidRowSection(row);
  return section ? buildVendorIsLineKey(section, row.account) : '';
}

// Resolve one TEMPLATE row to its IS line. Returns { lineKey, reason, ... }:
//   - row.is_line present       -> { lineKey: row.is_line, reason: 'slug' }       (a line already chosen: slug-first, as above)
//   - NO section on the row     -> resolvePrepaidContractLine(row, labelToLine)   (today's name-only rule, unchanged:
//                                  'label' | 'no-match' | 'ambiguous')
//   - a section on the row      -> the vendor rule, and the key must be a line of THIS company:
//       a leaf has that key     -> { lineKey: <key>, reason: 'section', section }
//       no leaf has it          -> { lineKey: null, reason: 'section-no-match', section, key }
//       not one of the sections -> { lineKey: null, reason: 'bad-section', section }
//     A row that names its section NEVER falls back to the name-only rule: "Software" under G&A must not quietly book
//     to the one "Software" some other section happens to have.
// `labelToLine` is buildPrepaidLabelToLine's map; a key is unique, so a section + name is one leaf or none.
// NEVER warns, NEVER books.
export function resolvePrepaidRowLine(row, labelToLine) {
  if (row.is_line) return { lineKey: row.is_line, reason: 'slug' };
  const section = prepaidRowSection(row);
  if (!section) return resolvePrepaidContractLine(row, labelToLine);
  if (!CAT_SLUG_BY_DISPLAY[section]) return { lineKey: null, reason: 'bad-section', section };
  const key = prepaidRowSectionKey(row);
  if (key) for (const ids of labelToLine.values()) if (ids.has(key)) return { lineKey: key, reason: 'section', section };
  return { lineKey: null, reason: 'section-no-match', section, key };
}

// ---- A STORED CONTRACT WITH NO LINE (2026-10-29, Step-6 line loss) ------------------------------------------------
// A contract read back with no is_line resolves by its account label alone, and a label a chart repeats across sections
// ("Software" under COGS, G&A and R&D) names no single line. When the contract is LINKED to a vendor (its stored
// vendor_id) and that vendor's own line is one of the label's lines, the link is the stored answer: returns that line.
// null otherwise -- the contract carries is_line, its label resolves by itself (or to nothing), it has no link, or the
// vendor's line is not one of the label's lines (never a guess outside what the label already allows).
// `vendorLine` is the linked vendor's is_line (the caller looks it up by vendor_id). NEVER warns, NEVER books.
export function prepaidLineFromVendorLink(contract, vendorLine, labelToLine) {
  if (!contract || contract.is_line || !vendorLine) return null;
  const r = resolvePrepaidContractLine({ account: contract.account, is_line: null }, labelToLine);
  return r.reason === 'ambiguous' && r.ids.includes(vendorLine) ? vendorLine : null;
}

// A parsed row, copied, carrying the line its section + name resolve to as `is_line` -- so the slug-first path above
// (the Step-6 import, the prepaid editor, the engine) books it with no second rule. A row with no section, or whose
// section + name is not a line of this company, is copied unchanged.
export function stampPrepaidRowLine(row, labelToLine) {
  const r = resolvePrepaidRowLine(row, labelToLine);
  return r.reason === 'section' ? { ...row, is_line: r.lineKey } : { ...row };
}
